import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Tag, TagSearchFilterParam, TagsParams } from '../../interfaces/api/tag';
import { fetchTags, fetchTagsSearchFilter, updateTag } from '../services/tags.service';
import { fetchRoomSearchFilterThunk } from './roomSlice';

interface TagState {
	tags: {
		[id: number]: Tag;
	};
	isLoading: boolean;
}

const initialState = {
	tags: [],
	isLoading: false,
} as TagState;

export const fetchTagsThunk = createAsyncThunk<Tag[], void>(
	'tag/fetchTags',
	async (_undefined, { rejectWithValue }) => {
		try {
			return await fetchTags();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchTagSearchFilterThunk = createAsyncThunk<Tag[], { params: TagSearchFilterParam }>(
	'tag/fetchTagsSearchFilter',
	async ({ params }, { rejectWithValue }) => {
		try {
			return await fetchTagsSearchFilter(params);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const updateTagThunk = createAsyncThunk<Tag, { id: number; param: TagsParams }>(
	'tag/updateTag',
	async ({ id, param }, { rejectWithValue }) => {
		try {
			return await updateTag(id, param);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const tagSlice = createSlice({
	name: 'tag',
	initialState,
	reducers: {
		resetTag: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchTagsThunk.fulfilled, (state, action) => {
			const ret: { [id: number]: Tag } = {};
			state.tags = action.payload.reduce((obj, tag) => {
				obj[tag.id] = tag;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchTagsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchTagsThunk.rejected, (state, action) => {
			state.tags = [];
			state.isLoading = false;
		});

		builder.addCase(fetchTagSearchFilterThunk.fulfilled, (state, action) => {
			const ret: { [id: number]: Tag } = {};
			state.tags = action.payload.reduce((obj, tag) => {
				obj[tag.id] = tag;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchRoomSearchFilterThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRoomSearchFilterThunk.rejected, (state, action) => {
			state.tags = [];
			state.isLoading = false;
		});

		builder.addCase(updateTagThunk.fulfilled, (state, action) => {
			state.tags[action.payload.id] = action.payload;
			state.isLoading = false;
		});
		builder.addCase(updateTagThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(updateTagThunk.rejected, (state) => {
			state.isLoading = false;
		});
	},
});

export const { resetTag } = tagSlice.actions;

export default tagSlice;
